'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { Hexagon } from 'lucide-react'
import { buttonVariants } from '@/core/components/button'

/**
 * Root error boundary. Without this, any uncaught server-render or client error
 * fell through to Next.js's raw default error page (an unstyled stack/digest
 * dump — the "screen full of code" QA reported). This renders a branded,
 * recoverable fallback instead.
 */
export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    console.error('[root error boundary]', error)
  }, [error])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 py-16">
      <div className="h-16 w-16 rounded-full bg-secondary flex items-center justify-center mb-4">
        <Hexagon className="h-8 w-8 text-muted-foreground" />
      </div>
      <p className="text-lg font-semibold">Something went wrong</p>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm">
        We hit an unexpected error loading this page. Please try again — if it keeps happening, head
        back home.
      </p>
      <div className="mt-6 flex gap-2">
        <button onClick={reset} className={buttonVariants()}>
          Try again
        </button>
        <Link href="/" className={buttonVariants({ variant: 'outline' })}>
          Home
        </Link>
      </div>
    </div>
  )
}
